import {
  AlgorithmTraceCallback,
  Chromosome,
  Particle,
} from '../types/algorithm';
import {
  AlgorithmTraceRecord,
  OptimizationParameters,
  OptimizationResults,
  OptimizationResultsWithStats,
  AlgorithmResult,
  ChartDataPoint,
  FitnessMode,
  EnergyAwareWeights,
} from '../types';
import { parseQWSDataset, buildQualityMatrix } from '../utils/qwsParser';
import { calculateAggregatedQoS, calculateResponseTime, calculateEnergy, calculateCost, FitnessCalculator, CostCalculator, setGlobalFitnessCalculator, clearGlobalFitnessCalculator, setGlobalCostCalculator, clearGlobalCostCalculator, DEFAULT_ENERGY_AWARE_WEIGHTS } from '../utils/fitnessCalculator';
import { generateRunSeed } from '../utils/seededRNG';
import { runGeneticAlgorithm } from '../algorithms/geneticAlgorithm';
import { runCulturalAlgorithm } from '../algorithms/culturalAlgorithm';
import { runSFLA } from '../algorithms/sfla';
import { runSFLAGABaseline } from '../algorithms/sflaGaBaseline';
import { runSFLAGA } from '../algorithms/sflaga';
import { runPSO } from '../algorithms/pso';
import { runGAPSO } from '../algorithms/gapso';
import { calculateAlgorithmStatistics } from '../utils/statistics';
import { normalizeAcrossAlgorithms } from '../utils/normalization';
import { calculateCompositeScoreStatistics, DEFAULT_COMPOSITE_WEIGHTS } from '../utils/compositeScore';
import { performParetoAnalysis } from '../utils/paretoAnalysis';
import { SHARED_PARAMETER_POLICY, resolveSflagaEnhancements } from '../utils/parameterUtils';
import { normalizeEnergyAwareWeights } from '../utils/energyAwareWeights';
import { ParallelOptimizationRunner } from './parallelOptimizationRunner';

export interface ConvergenceData {
  ga: ChartDataPoint[];
  culture: ChartDataPoint[];
  frog: ChartDataPoint[];
  sflaGa?: ChartDataPoint[];
  sflaga: ChartDataPoint[];
  pso: ChartDataPoint[];
  gapso: ChartDataPoint[];
}

export class OptimizationService {
  private matrix: number[][][] | null = null;
  private services: any[] = [];
  private taskCount: number = 0;
  private serviceCount: number = 0;
  private fitnessCalculator: FitnessCalculator | null = null;
  private costCalculator: CostCalculator | null = null;
  private fitnessMode: FitnessMode = 'energy-aware';
  private energyAwareWeights: EnergyAwareWeights = DEFAULT_ENERGY_AWARE_WEIGHTS;
  private matrixSeed: number | null = null; // Seed used for QWS matrix generation (for reproducibility)
  private parallelRunner = new ParallelOptimizationRunner();

  /**
   * Loads and parses QWS dataset
   * @param csvText CSV text content
   * @param taskCount Number of tasks
   * @param fitnessMode Fitness calculation mode (default: 'energy-aware')
   * @param energyAwareWeights Weights for energy-aware mode (only used if fitnessMode is 'energy-aware')
   * @param seed Optional seed for QWS matrix generation. If not provided, generates a deterministic seed.
   *             For thesis experiments, provide a fixed seed and log it for reproducibility.
   */
  loadDataset(
    csvText: string, 
    taskCount: number,
    fitnessMode: FitnessMode = 'energy-aware',
    energyAwareWeights: EnergyAwareWeights = DEFAULT_ENERGY_AWARE_WEIGHTS,
    seed?: number,
    taskSpecificVariationEnabled: boolean = true
  ): void {
    // Clean up previous calculators to avoid stale state
    this.dispose();
    
    this.services = parseQWSDataset(csvText);
    this.serviceCount = this.services.length;
    this.taskCount = taskCount;
    
    // Build quality matrix with seeded RNG for reproducibility
    const { matrix, seed: actualSeed } = buildQualityMatrix(
      this.services,
      taskCount,
      seed,
      taskSpecificVariationEnabled
    );
    this.matrix = matrix;
    this.matrixSeed = actualSeed;
    
    // Log seed for reproducibility (critical for thesis experiments)
    console.log(
      `[OptimizationService] QWS Matrix built with seed: ${actualSeed} (taskCount: ${taskCount}, services: ${this.serviceCount}, variation: ${taskSpecificVariationEnabled ? 'on' : 'off'})`
    );
    
    const resolvedEnergyAwareWeights = normalizeEnergyAwareWeights(energyAwareWeights);

    this.fitnessMode = fitnessMode;
    this.energyAwareWeights = resolvedEnergyAwareWeights;
    
    // Initialize cost calculator with cached service prices (PERFORMANCE OPTIMIZATION)
    // This must be initialized before fitness calculator if using energy-aware mode
    if (this.matrix) {
      this.costCalculator = new CostCalculator();
      this.costCalculator.initialize(this.matrix, taskCount, this.serviceCount);
      setGlobalCostCalculator(this.costCalculator);
      
      // Initialize fitness calculator with cached min/max values (PERFORMANCE OPTIMIZATION)
      // For energy-aware mode, also precomputes the energy range
      // Use matrix seed for fitness calculator sampling (reproducibility)
      this.fitnessCalculator = new FitnessCalculator();
      this.fitnessCalculator.initialize(
        this.matrix, 
        taskCount, 
        this.serviceCount,
        fitnessMode,
        resolvedEnergyAwareWeights,
        this.costCalculator,
        actualSeed // Pass seed for reproducible sampling
      );
      setGlobalFitnessCalculator(this.fitnessCalculator);
    }
  }
  
  /**
   * Gets the seed used for the current QWS matrix
   * @returns The seed used for matrix generation, or null if no matrix has been built yet
   */
  getMatrixSeed(): number | null {
    return this.matrixSeed;
  }

  /**
   * Disposes calculators and clears global state
   * Call this when dataset/taskCount changes to avoid stale state
   */
  dispose(): void {
    this.parallelRunner.cancel();

    // Clear caches
    if (this.fitnessCalculator) {
      this.fitnessCalculator.clearCache();
    }
    
    // Clear global calculators
    clearGlobalFitnessCalculator();
    clearGlobalCostCalculator();
    
    // Reset references
    this.fitnessCalculator = null;
    this.costCalculator = null;
  }

  cancelActiveWork(): void {
    this.parallelRunner.cancel();
  }

  /**
   * Runs all optimization algorithms
   */
  async runOptimization(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number, point?: ChartDataPoint) => void,
    onAlgorithmComplete?: (algorithm: string, result: AlgorithmResult, partialResults: Partial<OptimizationResults>) => void,
    shouldCancel?: () => boolean,
    runNumber: number = 1
  ): Promise<{
    results: OptimizationResults;
    convergenceData: ConvergenceData;
    traceRecords?: AlgorithmTraceRecord[];
  }> {
    if (!this.matrix) {
      throw new Error('Dataset not loaded');
    }

    const recordMode = parameters.recordMode ?? 'off';

    const requestedParallelExecution = parameters.parallelExecution ?? false;
    const effectiveParallelExecution = requestedParallelExecution && recordMode === 'off';

    if (recordMode === 'off' && typeof Worker !== 'undefined') {
      const includeSflaGaBaseline = parameters.includeSflaGaBaseline ?? false;
      const sflagaEnhancements = resolveSflagaEnhancements(parameters.sflagaEnhancements);
      const baseSeed = this.matrixSeed ?? Math.floor(Date.now() % 2147483647);
      const runSeed = generateRunSeed(baseSeed, runNumber);
      const fitnessMode = parameters.fitnessMode ?? this.fitnessMode;
      const rawEnergyAwareWeights = parameters.energyAwareWeights ?? this.energyAwareWeights;
      const energyAwareWeights = normalizeEnergyAwareWeights(rawEnergyAwareWeights);
      const normalizedParameters: OptimizationParameters = {
        ...parameters,
        sflagaEnhancements,
      };

      return this.parallelRunner.run({
        matrix: this.matrix,
        taskCount: this.taskCount,
        serviceCount: this.serviceCount,
        parameters: normalizedParameters,
        includeSflaGaBaseline,
        fitnessMode,
        energyAwareWeights,
        matrixSeed: this.matrixSeed,
        runSeed,
        onAlgorithmStart,
        onGenerationUpdate,
        onAlgorithmComplete,
        shouldCancel,
        workerCountOverride: requestedParallelExecution ? undefined : 1,
      });
    }

    const includeSflaGaBaseline = parameters.includeSflaGaBaseline ?? false;
    const sflagaEnhancements = resolveSflagaEnhancements(parameters.sflagaEnhancements);
    const traceRecords: AlgorithmTraceRecord[] = [];
    let traceSequence = 0;
    const lastUiUpdateByAlgorithm: Record<string, number> = {};
    const previousSummaryFitnessByAlgorithm: Record<string, number | undefined> = {};
    const shouldStreamEveryGeneration = parameters.iterations <= 50;

    const getElapsedExecutionTime = (startedAt: number) => performance.now() - startedAt;
    
    // Generate seed for this run (reproducible per run)
    // Use matrix seed as base, then derive run-specific seed
    const baseSeed = this.matrixSeed ?? Math.floor(Date.now() % 2147483647);
    const runSeed = generateRunSeed(baseSeed, runNumber);

    // Update fitness mode and weights if provided in parameters
    const fitnessMode = parameters.fitnessMode ?? this.fitnessMode;
    const rawEnergyAwareWeights = parameters.energyAwareWeights ?? this.energyAwareWeights;
    const energyAwareWeights = normalizeEnergyAwareWeights(rawEnergyAwareWeights);

    // Reinitialize calculators if mode or weights changed
    if (fitnessMode !== this.fitnessMode || 
        (fitnessMode === 'energy-aware' && 
         (energyAwareWeights.fitness !== this.energyAwareWeights.fitness ||
          energyAwareWeights.energy !== this.energyAwareWeights.energy))) {
      if (this.fitnessCalculator && this.matrix) {
        this.fitnessMode = fitnessMode;
        this.energyAwareWeights = energyAwareWeights;
        this.fitnessCalculator.initialize(
          this.matrix,
          this.taskCount,
          this.serviceCount,
          fitnessMode,
          energyAwareWeights,
          this.costCalculator,
          this.matrixSeed ?? undefined
        );
        setGlobalFitnessCalculator(this.fitnessCalculator);
      }
    }

    const convergenceData: ConvergenceData = {
      ga: [],
      culture: [],
      frog: [],
      sflaGa: [],
      sflaga: [],
      pso: [],
      gapso: [],
    };

    const createConvergencePoint = (
      generation: number,
      fitness: number,
      solution?: number[],
      executionTime?: number
    ): ChartDataPoint => {
      const point: ChartDataPoint = { iteration: generation, fitness };

      if (Number.isFinite(executionTime)) {
        point.executionTime = executionTime;
      }

      if (solution) {
        point.aggregatedQos = calculateAggregatedQoS(solution, this.matrix!, this.taskCount, this.serviceCount);
        point.energy = calculateEnergy(solution, this.matrix!, this.taskCount, this.serviceCount);
        point.cost = calculateCost(solution, this.matrix!, this.taskCount, this.serviceCount);
        point.responseTime = calculateResponseTime(solution, this.matrix!, this.taskCount, this.serviceCount);
      }

      return point;
    };

    const recordGeneration = (
      key: keyof ConvergenceData,
      algorithm: string,
      generation: number,
      fitness: number,
      solution?: number[],
      executionTime?: number
    ) => {
      const point = createConvergencePoint(generation, fitness, solution, executionTime);
      convergenceData[key]?.push(point);
      if (recordMode !== 'off') {
        const previousFitness = previousSummaryFitnessByAlgorithm[algorithm];
        traceRecords.push({
          sequence: ++traceSequence,
          algorithm,
          runNumber,
          generation,
          phase: 'progress',
          action: 'generation_summary',
          detail: 'Recorded generation-level progress metrics.',
          previousFitness: previousFitness ?? fitness,
          fitness,
          bestFitness: fitness,
          accepted: previousFitness === undefined ? true : fitness > previousFitness,
          solution,
          elapsedTime: executionTime,
          metadata: {
            aggregatedQos: point.aggregatedQos,
            energy: point.energy,
            cost: point.cost,
            responseTime: point.responseTime,
          },
        });
        previousSummaryFitnessByAlgorithm[algorithm] = fitness;
      }
      const now = performance.now();
      const lastUiUpdate = lastUiUpdateByAlgorithm[algorithm] ?? 0;
      const shouldNotifyUi =
        recordMode === 'off' ||
        shouldStreamEveryGeneration ||
        now - lastUiUpdate > 100 ||
        generation % 10 === 9 ||
        generation === parameters.iterations;

      if (onGenerationUpdate && shouldNotifyUi) {
        onGenerationUpdate(algorithm, generation, fitness, point);
        lastUiUpdateByAlgorithm[algorithm] = now;
      }
    };

    const createTraceCallback = (
      algorithm: string,
      getElapsedTime: () => number
    ): AlgorithmTraceCallback | undefined => {
      if (recordMode !== 'full') {
        return undefined;
      }

      return (event) => {
        traceRecords.push({
          sequence: ++traceSequence,
          algorithm,
          runNumber,
          elapsedTime: getElapsedTime(),
          ...event,
        });
      };
    };

    const runtimeOptions = (
      algorithm: string,
      getElapsedTime: () => number
    ) => recordMode === 'off'
      ? {
          yieldForProgress: true,
          reportEveryGeneration: shouldStreamEveryGeneration,
        }
      : {
          yieldForProgress: true,
          reportEveryGeneration: true,
          trace: createTraceCallback(algorithm, getElapsedTime),
        };

    const replayRuntimeOptions = {
      yieldForProgress: false,
      reportEveryGeneration: false,
    };

    const clearFitnessCacheForTiming = () => {
      this.fitnessCalculator?.clearCache();
    };

    const measureReplay = async <T,>(run: () => Promise<T>): Promise<{ result: T; executionTime: number }> => {
      if (shouldCancel?.()) {
        throw new Error('Optimization cancelled');
      }
      clearFitnessCacheForTiming();
      const startedAt = performance.now();
      const result = await run();
      return {
        result,
        executionTime: performance.now() - startedAt,
      };
    };

    // Check for cancellation before starting GA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run GA
    if (onAlgorithmStart) onAlgorithmStart('GA');
    clearFitnessCacheForTiming();
    const gaStartTime = performance.now();
    const gaTraceResult = await runGeneticAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness, solution) => {
        recordGeneration('ga', 'GA', gen, fitness, solution, getElapsedExecutionTime(gaStartTime));
      },
      runSeed, // Pass seed for reproducibility
      runtimeOptions('GA', () => getElapsedExecutionTime(gaStartTime))
    );
    const gaMeasured = recordMode !== 'off'
      ? await measureReplay(() => runGeneticAlgorithm(
          this.taskCount,
          this.serviceCount,
          this.matrix!,
          {
            populationSize: parameters.initialPopulation,
            generations: parameters.iterations,
            selectionRate: parameters.selectionRate,
            crossoverRate: parameters.crossoverRate,
            mutationRate: parameters.mutationRate,
          },
          undefined,
          runSeed,
          replayRuntimeOptions
        ))
      : { result: gaTraceResult, executionTime: getElapsedExecutionTime(gaStartTime) };
    const gaResult = gaMeasured.result;
    const gaExecutionTime = gaMeasured.executionTime;
    
    // Convert and notify GA completion
    const convertChromosomeToResult = (chrom: Chromosome, execTime: number): AlgorithmResult => ({
      fitness: chrom.fitness,
      solution: chrom.x,
      aggregatedQos: calculateAggregatedQoS(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      energy: calculateEnergy(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });
    
    const gaResultConverted = convertChromosomeToResult(gaResult, gaExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('GA', gaResultConverted, { ga: gaResultConverted });
    }

    // Check for cancellation before starting Cultural Algorithm
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run Cultural Algorithm
    if (onAlgorithmStart) onAlgorithmStart('Cultural');
    clearFitnessCacheForTiming();
    const cultureStartTime = performance.now();
    const cultureTraceResult = await runCulturalAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness, solution) => {
        recordGeneration('culture', 'Cultural', gen, fitness, solution, getElapsedExecutionTime(cultureStartTime));
      },
      runSeed, // Pass seed for reproducibility
      runtimeOptions('Cultural', () => getElapsedExecutionTime(cultureStartTime))
    );
    const cultureMeasured = recordMode !== 'off'
      ? await measureReplay(() => runCulturalAlgorithm(
          this.taskCount,
          this.serviceCount,
          this.matrix!,
          {
            populationSize: parameters.initialPopulation,
            generations: parameters.iterations,
            selectionRate: parameters.selectionRate,
            crossoverRate: parameters.crossoverRate,
            mutationRate: parameters.mutationRate,
          },
          undefined,
          runSeed,
          replayRuntimeOptions
        ))
      : { result: cultureTraceResult, executionTime: getElapsedExecutionTime(cultureStartTime) };
    const cultureResult = cultureMeasured.result;
    const cultureExecutionTime = cultureMeasured.executionTime;
    
    const cultureResultConverted = convertChromosomeToResult(cultureResult, cultureExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('Cultural', cultureResultConverted, { ga: gaResultConverted, culture: cultureResultConverted });
    }

    // Check for cancellation before starting SFLA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run SFLA
    if (onAlgorithmStart) onAlgorithmStart('SFLA');
    clearFitnessCacheForTiming();
    const frogStartTime = performance.now();
    const frogTraceResult = await runSFLA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
      },
      (gen, fitness, solution) => {
        recordGeneration('frog', 'SFLA', gen, fitness, solution, getElapsedExecutionTime(frogStartTime));
      },
      runSeed, // Pass seed for reproducibility
      runtimeOptions('SFLA', () => getElapsedExecutionTime(frogStartTime))
    );
    const frogMeasured = recordMode !== 'off'
      ? await measureReplay(() => runSFLA(
          this.taskCount,
          this.serviceCount,
          this.matrix!,
          {
            populationSize: parameters.initialPopulation,
            generations: parameters.iterations,
            memeplexCount: parameters.memeplexCount,
          },
          undefined,
          runSeed,
          replayRuntimeOptions
        ))
      : { result: frogTraceResult, executionTime: getElapsedExecutionTime(frogStartTime) };
    const frogResult = frogMeasured.result;
    const frogExecutionTime = frogMeasured.executionTime;
    
    const frogResultConverted = convertChromosomeToResult(frogResult, frogExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('SFLA', frogResultConverted, { ga: gaResultConverted, culture: cultureResultConverted, frog: frogResultConverted });
    }

    let sflaGaResultConverted: AlgorithmResult | undefined;

    if (includeSflaGaBaseline) {
      // Check for cancellation before starting the standard SFLA-GA baseline
      if (shouldCancel && shouldCancel()) {
        throw new Error('Optimization cancelled');
      }

      if (onAlgorithmStart) onAlgorithmStart('SFLA-GA');
      clearFitnessCacheForTiming();
      const sflaGaStartTime = performance.now();
      const sflaGaTraceResult = await runSFLAGABaseline(
        this.taskCount,
        this.serviceCount,
        this.matrix,
        {
          populationSize: parameters.initialPopulation,
          generations: parameters.iterations,
          memeplexCount: parameters.memeplexCount,
          selectionRate: parameters.selectionRate,
          crossoverRate: parameters.crossoverRate,
          mutationRate: parameters.mutationRate,
        },
        (gen, fitness, solution) => {
          convergenceData.sflaGa = convergenceData.sflaGa ?? [];
          recordGeneration('sflaGa', 'SFLA-GA', gen, fitness, solution, getElapsedExecutionTime(sflaGaStartTime));
        },
        runSeed, // Pass seed for reproducibility
        runtimeOptions('SFLA-GA', () => getElapsedExecutionTime(sflaGaStartTime))
      );
      const sflaGaMeasured = recordMode !== 'off'
        ? await measureReplay(() => runSFLAGABaseline(
            this.taskCount,
            this.serviceCount,
            this.matrix!,
            {
              populationSize: parameters.initialPopulation,
              generations: parameters.iterations,
              selectionRate: parameters.selectionRate,
              memeplexCount: parameters.memeplexCount,
              crossoverRate: parameters.crossoverRate,
              mutationRate: parameters.mutationRate,
            },
            undefined,
            runSeed,
            replayRuntimeOptions
          ))
        : { result: sflaGaTraceResult, executionTime: getElapsedExecutionTime(sflaGaStartTime) };
      const sflaGaResult = sflaGaMeasured.result;
      const sflaGaExecutionTime = sflaGaMeasured.executionTime;

      sflaGaResultConverted = convertChromosomeToResult(sflaGaResult, sflaGaExecutionTime);
      if (onAlgorithmComplete) {
        onAlgorithmComplete('SFLA-GA', sflaGaResultConverted, {
          ga: gaResultConverted,
          culture: cultureResultConverted,
          frog: frogResultConverted,
          sflaGa: sflaGaResultConverted,
        });
      }
    }

    // Check for cancellation before starting the proposed SFLAGA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }

    if (onAlgorithmStart) onAlgorithmStart('SFLAGA');
    clearFitnessCacheForTiming();
    const sflagaStartTime = performance.now();
    const sflagaTraceResult = await runSFLAGA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
        heavyMode: parameters.heavyMode,
        sflagaEnhancements,
      },
      (gen, fitness, solution) => {
        recordGeneration('sflaga', 'SFLAGA', gen, fitness, solution, getElapsedExecutionTime(sflagaStartTime));
      },
      runSeed, // Pass seed for reproducibility
      runtimeOptions('SFLAGA', () => getElapsedExecutionTime(sflagaStartTime))
    );
    const sflagaMeasured = recordMode !== 'off'
      ? await measureReplay(() => runSFLAGA(
          this.taskCount,
          this.serviceCount,
          this.matrix!,
          {
            populationSize: parameters.initialPopulation,
            generations: parameters.iterations,
            memeplexCount: parameters.memeplexCount,
            selectionRate: parameters.selectionRate,
            crossoverRate: parameters.crossoverRate,
            mutationRate: parameters.mutationRate,
            heavyMode: parameters.heavyMode,
            sflagaEnhancements,
          },
          undefined,
          runSeed,
          replayRuntimeOptions
        ))
      : { result: sflagaTraceResult, executionTime: getElapsedExecutionTime(sflagaStartTime) };
    const sflagaResult = sflagaMeasured.result;
    const sflagaExecutionTime = sflagaMeasured.executionTime;

    const sflagaResultConverted = convertChromosomeToResult(sflagaResult, sflagaExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('SFLAGA', sflagaResultConverted, {
        ga: gaResultConverted,
        culture: cultureResultConverted,
        frog: frogResultConverted,
        ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
        sflaga: sflagaResultConverted,
      });
    }

    // Check for cancellation before starting PSO
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run PSO
    if (onAlgorithmStart) onAlgorithmStart('PSO');
    clearFitnessCacheForTiming();
    const psoStartTime = performance.now();
    const psoTraceResult = await runPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
      },
      (gen, fitness, solution) => {
        recordGeneration('pso', 'PSO', gen, fitness, solution, getElapsedExecutionTime(psoStartTime));
      },
      runSeed, // Pass seed for reproducibility
      runtimeOptions('PSO', () => getElapsedExecutionTime(psoStartTime))
    );
    const psoMeasured = recordMode !== 'off'
      ? await measureReplay(() => runPSO(
          this.taskCount,
          this.serviceCount,
          this.matrix!,
          {
            populationSize: parameters.initialPopulation,
            generations: parameters.iterations,
          },
          undefined,
          runSeed,
          replayRuntimeOptions
        ))
      : { result: psoTraceResult, executionTime: getElapsedExecutionTime(psoStartTime) };
    const psoResult = psoMeasured.result;
    const psoExecutionTime = psoMeasured.executionTime;
    
    const convertParticleToResult = (particle: Particle, execTime: number): AlgorithmResult => ({
      fitness: particle.fitness,
      solution: particle.position,
      aggregatedQos: calculateAggregatedQoS(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      energy: calculateEnergy(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });
    
    const psoResultConverted = convertParticleToResult(psoResult, psoExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('PSO', psoResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
        sflaga: sflagaResultConverted,
        pso: psoResultConverted 
      });
    }

    // Check for cancellation before starting GAPSO
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run GAPSO
    if (onAlgorithmStart) onAlgorithmStart('GAPSO');
    clearFitnessCacheForTiming();
    const gapsoStartTime = performance.now();
    const gapsoTraceResult = await runGAPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness, solution) => {
        recordGeneration('gapso', 'GAPSO', gen, fitness, solution, getElapsedExecutionTime(gapsoStartTime));
      },
      runSeed, // Pass seed for reproducibility
      runtimeOptions('GAPSO', () => getElapsedExecutionTime(gapsoStartTime))
    );

    const gapsoMeasured = recordMode !== 'off'
      ? await measureReplay(() => runGAPSO(
          this.taskCount,
          this.serviceCount,
          this.matrix!,
          {
            populationSize: parameters.initialPopulation,
            generations: parameters.iterations,
            selectionRate: parameters.selectionRate,
            crossoverRate: parameters.crossoverRate,
            mutationRate: parameters.mutationRate,
          },
          undefined,
          runSeed,
          replayRuntimeOptions
        ))
      : { result: gapsoTraceResult, executionTime: getElapsedExecutionTime(gapsoStartTime) };
    const gapsoResult = gapsoMeasured.result;
    const gapsoExecutionTime = gapsoMeasured.executionTime;
    
    const gapsoResultConverted = convertParticleToResult(gapsoResult, gapsoExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('GAPSO', gapsoResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
        sflaga: sflagaResultConverted,
        pso: psoResultConverted,
        gapso: gapsoResultConverted 
      });
    }

    const results: OptimizationResults = {
      ga: convertChromosomeToResult(gaResult, gaExecutionTime),
      culture: convertChromosomeToResult(cultureResult, cultureExecutionTime),
      frog: convertChromosomeToResult(frogResult, frogExecutionTime),
      ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
      sflaga: convertChromosomeToResult(sflagaResult, sflagaExecutionTime),
      pso: convertParticleToResult(psoResult, psoExecutionTime),
      gapso: convertParticleToResult(gapsoResult, gapsoExecutionTime),
      configuration: {
        taskCount: parameters.taskCount,
        propertyCount: parameters.qosProperties,
        initialPopulation: parameters.initialPopulation,
        iterations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossover: parameters.crossoverRate,
        mutation: parameters.mutationRate,
        memeplexCount: parameters.memeplexCount,
        includeSflaGaBaseline,
        parallelExecution: effectiveParallelExecution,
        heavyMode: parameters.heavyMode ?? false,
        fitnessMode: parameters.fitnessMode,
        energyAwareWeights: fitnessMode === 'energy-aware' ? energyAwareWeights : undefined,
        sflagaEnhancements,
        recordMode,
        parameterPolicy: parameters.parameterPolicy ?? SHARED_PARAMETER_POLICY,
        seed: this.matrixSeed ?? undefined, // Include matrix seed for reproducibility
        runSeed: runSeed, // Include run seed for reproducibility
        taskSpecificVariationEnabled: parameters.taskSpecificVariationEnabled ?? true,
      },
    };

    return {
      results,
      convergenceData,
      ...(recordMode !== 'off' ? { traceRecords } : {}),
    };
  }

  /**
   * Runs optimization multiple times and calculates statistics
   * @param skipHeavyCalculations - If true, skips normalization, composite scores, and Pareto analysis (for complete run optimization)
   */
  async runOptimizationWithStatistics(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number, point?: ChartDataPoint) => void,
    onRunComplete?: (runNumber: number, totalRuns: number, intermediateResults?: OptimizationResultsWithStats) => void,
    onAlgorithmComplete?: (algorithm: string, result: AlgorithmResult, partialResults: Partial<OptimizationResults>) => void,
    shouldCancel?: () => boolean,
    skipHeavyCalculations: boolean = false
  ): Promise<{
    results: OptimizationResultsWithStats;
    convergenceData: ConvergenceData;
    traceRecords?: AlgorithmTraceRecord[];
  }> {
    const numberOfRuns = parameters.numberOfRuns || 1;
    const includeSflaGaBaseline = parameters.includeSflaGaBaseline ?? false;
    
    if (numberOfRuns === 1) {
      // Single run - no statistics needed
      const { results, convergenceData, traceRecords } = await this.runOptimization(
        parameters,
        onAlgorithmStart,
        onGenerationUpdate,
        onAlgorithmComplete,
        shouldCancel
      );
      return {
        results: {
          ...results,
          timestamp: new Date().toISOString(),
          runNumber: 1,
        },
        convergenceData,
        ...(traceRecords ? { traceRecords } : {}),
      };
    }

    // Multiple runs - collect results and calculate statistics
    const allRuns: OptimizationResults[] = [];
    let lastConvergenceData: ConvergenceData | null = null;
    const traceRecords: AlgorithmTraceRecord[] = [];
    const ensureAggregatedQos = (result: AlgorithmResult | undefined): AlgorithmResult | undefined => {
      if (!result || Number.isFinite(result.aggregatedQos)) {
        return result;
      }

      return {
        ...result,
        aggregatedQos: calculateAggregatedQoS(
          result.solution,
          this.matrix!,
          this.taskCount,
          this.serviceCount
        ),
      };
    };
    const ensureRunAggregatedQos = (runResults: OptimizationResults): OptimizationResults => ({
      ...runResults,
      ga: ensureAggregatedQos(runResults.ga)!,
      culture: ensureAggregatedQos(runResults.culture)!,
      frog: ensureAggregatedQos(runResults.frog)!,
      ...(runResults.sflaGa ? { sflaGa: ensureAggregatedQos(runResults.sflaGa) } : {}),
      sflaga: ensureAggregatedQos(runResults.sflaga)!,
      pso: ensureAggregatedQos(runResults.pso)!,
      gapso: ensureAggregatedQos(runResults.gapso)!,
    });

    for (let run = 0; run < numberOfRuns; run++) {
      // Check for cancellation before starting each run
      if (shouldCancel && shouldCancel()) {
        throw new Error('Optimization cancelled');
      }
      
      const { results, convergenceData, traceRecords: runTraceRecords } = await this.runOptimization(
        parameters,
        (algorithm) => {
          if (onAlgorithmStart) {
            onAlgorithmStart(`${algorithm} (Run ${run + 1}/${numberOfRuns})`);
          }
        },
        (algorithm, generation, fitness, point) => {
          if (onGenerationUpdate) {
            // Adjust progress to account for multiple runs
            // Each run contributes (100 / numberOfRuns) to overall progress
            // Within each run, we track generation progress as before
            // Extract just the algorithm name (remove run info)
            const algName = algorithm.split(' (Run')[0];
            onGenerationUpdate(algName, generation, fitness, point);
          }
        },
        (algorithm, result, partialResults) => {
          if (onAlgorithmComplete) {
            // Pass through the callback with algorithm name (without run suffix)
            const algName = algorithm.split(' (Run')[0];
            onAlgorithmComplete(algName, result, partialResults);
          }
        },
        shouldCancel,
        run + 1 // Pass run number for seed generation
      );

      const resultsWithAggregatedQos = ensureRunAggregatedQos(results);
      allRuns.push(resultsWithAggregatedQos);
      lastConvergenceData = convergenceData; // Store convergence data from last run
      if (runTraceRecords) {
        traceRecords.push(...runTraceRecords);
      }

      // OPTIMIZATION: During complete run, skip all intermediate heavy calculations
      // Only calculate final statistics at the end of all runs for this task count
      if (!skipHeavyCalculations) {
        // For regular optimization (not complete run), calculate intermediate statistics
        // Only calculate on last run or every 5 runs to reduce overhead
        const shouldCalculateIntermediate = run === numberOfRuns - 1 || (run + 1) % 5 === 0;
        
        if (shouldCalculateIntermediate) {
          // Calculate intermediate statistics after each run (PROGRESSIVE UPDATE)
          const intermediateStatistics: OptimizationResultsWithStats['statistics'] = {
            ga: calculateAlgorithmStatistics(allRuns.map(r => r.ga)),
            culture: calculateAlgorithmStatistics(allRuns.map(r => r.culture)),
            frog: calculateAlgorithmStatistics(allRuns.map(r => r.frog)),
            ...(includeSflaGaBaseline
              ? {
                  sflaGa: calculateAlgorithmStatistics(
                    allRuns
                      .map(r => r.sflaGa)
                      .filter((result): result is AlgorithmResult => Boolean(result))
                  ),
                }
              : {}),
            sflaga: calculateAlgorithmStatistics(allRuns.map(r => r.sflaga)),
            pso: calculateAlgorithmStatistics(allRuns.map(r => r.pso)),
            gapso: calculateAlgorithmStatistics(allRuns.map(r => r.gapso)),
          };

          // Calculate intermediate normalization and composite scores
          // Note: Normalization is done per task size (allRuns contains runs for a single task size)
          const intermediateNormalized = normalizeAcrossAlgorithms(allRuns);
          const intermediateCompositeScores = calculateCompositeScoreStatistics(intermediateNormalized, DEFAULT_COMPOSITE_WEIGHTS);

          // Calculate intermediate Pareto analysis
          const intermediateParetoAnalysis = performParetoAnalysis(allRuns);

          // Create intermediate results with current statistics
          const intermediateResults: OptimizationResultsWithStats = {
            ...resultsWithAggregatedQos,
            statistics: intermediateStatistics,
            compositeScores: intermediateCompositeScores.perAlgorithm,
            paretoAnalysis: intermediateParetoAnalysis,
            timestamp: new Date().toISOString(),
            runNumber: run + 1,
          };

          // Notify that a run is complete with intermediate results for UI update
          if (onRunComplete) {
            onRunComplete(run + 1, numberOfRuns, intermediateResults);
          }
        } else {
          // For non-intermediate calculation runs, just notify progress without statistics
          if (onRunComplete) {
            onRunComplete(run + 1, numberOfRuns, undefined);
          }
        }
      } else {
        // During complete run: skip all intermediate calculations, just notify progress
        // Heavy calculations (statistics, normalization, Pareto) will be done only at the end
        if (onRunComplete) {
          onRunComplete(run + 1, numberOfRuns, undefined);
        }
      }
    }

    // Final statistics - always calculated at the end (required for results)
    const statistics: OptimizationResultsWithStats['statistics'] = {
      ga: calculateAlgorithmStatistics(allRuns.map(r => r.ga)),
      culture: calculateAlgorithmStatistics(allRuns.map(r => r.culture)),
      frog: calculateAlgorithmStatistics(allRuns.map(r => r.frog)),
      ...(includeSflaGaBaseline
        ? {
            sflaGa: calculateAlgorithmStatistics(
              allRuns
                .map(r => r.sflaGa)
                .filter((result): result is AlgorithmResult => Boolean(result))
            ),
          }
        : {}),
      sflaga: calculateAlgorithmStatistics(allRuns.map(r => r.sflaga)),
      pso: calculateAlgorithmStatistics(allRuns.map(r => r.pso)),
      gapso: calculateAlgorithmStatistics(allRuns.map(r => r.gapso)),
    };

    // OPTIMIZATION: Skip heavy calculations during complete run
    // These will be calculated after complete run finishes if needed
    let normalizedResults: ReturnType<typeof normalizeAcrossAlgorithms> | null = null;
    let compositeScores: ReturnType<typeof calculateCompositeScoreStatistics> | null = null;
    let paretoAnalysis: ReturnType<typeof performParetoAnalysis> | null = null;

    if (!skipHeavyCalculations) {
      // Calculate normalization and composite scores (only for regular optimization)
      // IMPORTANT: Normalization is done per task size (allRuns contains runs for a single task size)
      // In complete runs, each task size is normalized independently, not across all task sizes
      normalizedResults = normalizeAcrossAlgorithms(allRuns);
      compositeScores = calculateCompositeScoreStatistics(normalizedResults, DEFAULT_COMPOSITE_WEIGHTS);

      // Perform Pareto analysis (only for regular optimization)
      paretoAnalysis = performParetoAnalysis(allRuns);
    }

    // Use the last run's results as the representative result
    const lastRun = allRuns[allRuns.length - 1];

    return {
      results: {
        ...lastRun,
        statistics,
        compositeScores: compositeScores?.perAlgorithm,
        paretoAnalysis: paretoAnalysis || undefined,
        timestamp: new Date().toISOString(),
        runNumber: numberOfRuns,
      },
      convergenceData: lastConvergenceData!,
      ...(parameters.recordMode && parameters.recordMode !== 'off' ? { traceRecords } : {}),
    };
  }
}
